import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { LoginService } from 'src/app/auth/login.service';
import { WithdrawRequestItem } from 'src/app/core/services/withdraw-request.service';
import { PaymentService } from 'src/app/core/services/payment.service';

@Component({
  selector: 'app-balance-dialog',
  templateUrl: './balance-dialog.component.html',
  styleUrls: ['./balance-dialog.component.css']
})
export class BalanceDialogComponent implements OnInit, OnDestroy, OnChanges {
  constructor(private loginService: LoginService, private paymentService: PaymentService) { }
  private readonly maxAmountInputLength = 12;
  depositAmountInput = '100,000';
  readonly depositPresetOptions = [100000, 200000, 500000, 1000000];
  private depositUserCode = '';
  private qrSessionNonce = Date.now();
  depositAmountNumber = 100000;
  depositAmountText = '';

  currentDate = new Date();
  step: 1 | 2 = 1;

  receiver = {
    name: 'LE VAN TU',
    account: '0568231174',
    bank: 'VPBank',
  };
  showDepositQr = false;
  showtQr = false;
  @Input() visible = false;
  @Input() formattedBalance = '0';
  @Input() withdrawSubmitting = false;
  @Input() withdrawSubmitMessage = '';
  @Input() withdrawSubmitError = false;

  withdrawSucceeded = false;

  ngOnInit(): void {
    this.depositUserCode = this.loginService.getUserCode() || '';
  }

  ngOnDestroy(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.withdrawSubmitMessage) {
      const msg = String(changes.withdrawSubmitMessage.currentValue || '');
      // If there's a non-empty message and no error flag, treat as success
      if (msg && !this.withdrawSubmitError) {
        this.withdrawSucceeded = true;
        // clear inputs on success
        this.withdrawAmountInput = '';
        this.passwordBankInput = '';
      } else if (!msg) {
        this.withdrawSucceeded = false;
      }
    }

    if (changes.withdrawSubmitError) {
      // If an error occurred, ensure the succeeded flag is cleared so user can retry
      if (changes.withdrawSubmitError.currentValue) {
        this.withdrawSucceeded = false;
      }
    }
  }

  showWithdrawForm = false;
  showHistoryDetails = false;
  withdrawAmountInput = '';
  passwordBankInput = '';
  showWithdrawPassword = false;
  withdrawError = '';
  @Input() historyPreview: WithdrawRequestItem[] = [];

  @Output() closed = new EventEmitter<void>();
  @Output() depositClicked = new EventEmitter<void>();
  @Output() withdrawClicked = new EventEmitter<void>();
  @Output() withdrawSubmitted = new EventEmitter<{ amount: number; password: string }>();

  get currentBalance(): number {
    const numericText = String(this.formattedBalance || '').replace(/\D/g, '');
    return Number(numericText || 0);
  }

  get formattedBalanceDisplay(): string {
    return this.formatWithCommas(this.currentBalance);
  }

  gettransactionCode(): string {
    const nameTransaction = encodeURIComponent(this.depositUserCode);
    const today = new Date();
    const dateOnly =
      today.getFullYear() +
      String(today.getMonth() + 1).padStart(2, '0') +
      String(today.getDate()).padStart(2, '0');
    const amount = this.depositAmountInput.replace(/\D/g, '');
    const transactionCode = `${nameTransaction}${amount}${dateOnly}`;
    return transactionCode;
  }
  get depositQrUrl(): string {
    const amount = this.parseNumeric(this.depositAmountInput) || 100000;
    const addInfo = encodeURIComponent(this.depositUserCode || 'nap-tien');
    return `https://img.vietqr.io/image/VPBank-0568231174-compact.png?amount=${amount}&addInfo=${this.depositToken}`;
  }

  getHistoryStatusLabel(status: string): string {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'pending') {
      return 'Đã gửi yêu cầu';
    }
    if (normalized === 'completed') {
      return 'Đã duyệt';
    }
    if (normalized === 'rejected') {
      return 'Đã từ chối';
    }
    return normalized || 'Unknown';
  }

  getHistoryStatusClass(status: string): string {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'pending') {
      return 'history-status history-status--pending';
    }
    if (normalized === 'approved') {
      return 'history-status history-status--approved';
    }
    if (normalized === 'rejected') {
      return 'history-status history-status--rejected';
    }
    return 'history-status history-status--default';
  }

  formatHistoryDate(dateValue: string): string {
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
      return '--';
    }

    return parsed.toLocaleDateString('vi-VN');
  }

  toggleHistoryDetails(): void {
    this.showHistoryDetails = !this.showHistoryDetails;
  }

  close(): void {
    this.showDepositQr = false;
    this.showWithdrawForm = false;
    this.withdrawAmountInput = '';
    this.passwordBankInput = '';
    this.showWithdrawPassword = false;
    this.withdrawError = '';
    this.withdrawSucceeded = false;
    this.closed.emit();
  }

  /** Payment token cache (4h) */
  private depositToken = '';
  private depositTokenExpireAt = 0;
  depositTokenLoading = false;
  depositTokenError = '';

  private hasValidDepositToken(): boolean {
    return !!this.depositToken && Date.now() < this.depositTokenExpireAt;
  }

  private async ensureDepositToken(): Promise<string> {
    if (this.hasValidDepositToken()) {
      return this.depositToken;
    }

    this.depositTokenLoading = true;
    this.depositTokenError = '';

    try {
      const token = await this.paymentService.getTokenMoney().toPromise();
      this.depositToken = token || '';
      // backend: 4h => cache 4h
      this.depositTokenExpireAt = Date.now() + 4 * 60 * 60 * 1000;
      return this.depositToken;
    } catch (e) {
      console.error('[BalanceDialog] getTokenMoney failed', e);
      this.depositTokenError = 'Không lấy được token thanh toán. Vui lòng thử lại.';
      this.depositToken = '';
      this.depositTokenExpireAt = 0;
      return '';
    } finally {
      this.depositTokenLoading = false;
    }
  }

  onDeposit(): void {
    this.showDepositQr = true;
    this.showWithdrawForm = false;
    this.withdrawAmountInput = '';
    this.withdrawError = '';
    // Không gọi token ở đây nữa, token sẽ được lấy ở confirmDeposit()
    this.depositClicked.emit();
  }

  onDepositAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    const value = input.value.replace(/\D/g, '');

    this.depositAmountNumber = Number(value || 0);

    this.depositAmountInput = this.formatWithCommas(this.depositAmountNumber);

    if (input.value !== this.depositAmountInput) {
      input.value = this.depositAmountInput;
    }

    this.depositAmountText = this.convertNumberToText(this.depositAmountNumber);
  }

  selectDepositAmount(amount: number): void {
    const value = Number(amount || 0);

    this.depositAmountNumber = value;
    this.depositAmountInput = this.formatWithCommas(value);

    this.depositAmountText = this.convertNumberToText(value);

    if (this.showDepositQr && value > 0) {
      this.reloadDepositQr();
    }
  }

  isDepositAmountActive(amount: number): boolean {
    return this.parseNumeric(this.depositAmountInput) === Number(amount || 0);
  }

  onWithdraw(): void {
    this.showDepositQr = false;
    this.showWithdrawForm = true;
    this.withdrawAmountInput = '';
    this.passwordBankInput = '';
    this.showWithdrawPassword = false;
    this.withdrawError = '';
    this.withdrawSucceeded = false;
    this.withdrawClicked.emit();
  }

  toggleWithdrawPasswordVisibility(): void {
    this.showWithdrawPassword = !this.showWithdrawPassword;
  }

  onWithdrawAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.withdrawAmountInput = this.clampFormattedNumericInput(input.value);
    if (input.value !== this.withdrawAmountInput) {
      input.value = this.withdrawAmountInput;
    }
    if (this.withdrawError) {
      this.withdrawError = '';
    }
  }

  onAmountKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const allowedKeys = new Set([
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ]);

    if (allowedKeys.has(event.key)) {
      return;
    }

    if (event.key.length === 1 && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPasswordBankInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.passwordBankInput = input.value || '';
    if (this.withdrawError) {
      this.withdrawError = '';
    }
  }

  submitWithdraw(): void {
    const amount = this.parseNumeric(this.withdrawAmountInput);
    if (!amount || amount <= 0) {
      this.withdrawError = 'Vui lòng nhập số tiền hợp lệ.';
      return;
    }

    if (!this.passwordBankInput.trim()) {
      this.withdrawError = 'Vui lòng nhập mật khẩu rút tiền.';
      return;
    }

    if (amount >= this.currentBalance) {
      this.withdrawError = 'Số tiền rút phải nhỏ hơn số dư hiện tại.';
      return;
    }

    this.withdrawError = '';
    this.withdrawSubmitted.emit({ amount, password: this.passwordBankInput.trim() });
  }

  async downloadDepositQr(): Promise<void> {
    try {
      const response = await fetch(this.depositQrUrl);
      if (!response.ok) {
        throw new Error(`Không tải được QR: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = 'vietqr-nap-tien.png';
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(error);
    }
  }

  private parseNumeric(rawValue: string): number {
    const digits = String(rawValue || '').replace(/\D/g, '');
    return Number(digits || 0);
  }

  private clampFormattedNumericInput(rawValue: string): string {
    let digits = String(rawValue || '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }

    while (digits.length > 0) {
      const numericValue = Number(digits);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return '';
      }

      const formatted = this.formatWithCommas(numericValue);
      if (formatted.length <= this.maxAmountInputLength) {
        return formatted;
      }

      digits = digits.slice(0, -1);
    }

    return '';
  }

  // Token có hạn sử dụng 4 giờ (backend). Chỉ lấy token khi user bấm xác nhận Nạp tiền.

  confirmDeposit(): void {
    // Lưu ý: token thanh toán có thời hạn chuyển tiền trong vòng 4h
    // => gọi API /payment/token tại đây để lấy token mới (hoặc dùng cache nếu còn hạn)
    this.ensureDepositToken();

    const amount = this.parseNumeric(this.depositAmountInput);

    if (!amount || amount <= 0) {
      return;
    }
    this.step = 2;       // 👈 chuyển bước
    this.showtQr = true; // 👈 bật QR
    this.showDepositQr = true;
    this.showtQr = true;
    this.reloadDepositQr(); // refresh QR
  }
  reloadDepositQr(): void {
    this.qrSessionNonce = Date.now();
  }

  private formatWithCommas(value: number): string {
    if (!Number.isFinite(value) || value <= 0) {
      return '0';
    }
    return new Intl.NumberFormat('en-US').format(value);
  }

  convertNumberToText(num: number): string {
  if (!num || num <= 0) return 'Không đồng';

  const ones = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];

  const readGroup = (n: number): string => {
    let str = '';
    const hundreds = Math.floor(n / 100);
    const tens = Math.floor((n % 100) / 10);
    const unit = n % 10;

    if (hundreds > 0) {
      str += ones[hundreds] + ' trăm ';
    }

    if (tens > 1) {
      str += ones[tens] + ' mươi ';
      if (unit === 1) str += 'mốt';
      else if (unit === 5) str += 'lăm';
      else if (unit > 0) str += ones[unit];
    } else if (tens === 1) {
      str += 'mười ';
      if (unit === 5) str += 'lăm';
      else if (unit > 0) str += ones[unit];
    } else if (tens === 0 && unit > 0) {
      if (hundreds > 0) str += 'lẻ ';
      str += ones[unit];
    }

    return str.trim();
  };

  const groups = ['', ' nghìn', ' triệu', ' tỷ'];
  let i = 0;
  let result = '';

  while (num > 0) {
    const group = num % 1000;
    if (group > 0) {
      result =
        readGroup(group) +
        groups[i] +
        ' ' +
        result;
    }
    num = Math.floor(num / 1000);
    i++;
  }

  return result.trim() + ' đồng';
}
closeDeposit(): void {
  this.resetDepositState();
  this.close(); // đóng dialog luôn
}

completeDeposit(): void {
  // có thể gọi API confirm ở đây nếu sau này cần

  this.resetDepositState();
  this.close();
}
private resetDepositState(): void {
  this.step = 1;
  this.showDepositQr = false;
  this.showtQr = false;

  this.depositAmountInput = '';
  this.depositAmountNumber = 0;
  this.depositAmountText = '';

  // reset token cache for safety (optional)
  this.depositToken = '';
  this.depositTokenExpireAt = 0;
  this.depositTokenLoading = false;
  this.depositTokenError = '';

  this.qrSessionNonce = Date.now();
}

}
